import { Card } from "@/components/flexkit/Card";
import { Text } from "@/components/podkit/typography/Text";
import { cn } from "@/components/podkit/lib/cn";
import type { PlainWorkflowExecutionActionOutput, PlainWorkflowExecutionAction } from "@/queries/automation-queries";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { AgentExecution_Status_OutputValue } from "gitpod-next-api/gitpod/v1/agent_pb";
import type { Workflow } from "gitpod-next-api/gitpod/v1/workflow_pb";
import type { FC } from "react";
import { useMemo } from "react";
import { ActionGroupType, groupActions } from "@/utils/automation-execution-action";
import { useExportWorkflowOutputsCSV } from "@/hooks/use-export-workflow-outputs-csv";
import { Button } from "@/components/flexkit/Button";

type PlainOutputValue = PlainMessage<AgentExecution_Status_OutputValue>;

type OutputSchema = {
    min: number;
    max: number;
};

type WorkflowExecutionOutputsTableProps = {
    outputs: PlainWorkflowExecutionActionOutput[];
    actions: PlainWorkflowExecutionAction[];
    workflow?: PlainMessage<Workflow>;
    workflowExecutionId: string;
};

/**
 * Extracts the display value from an OutputValue oneof field
 */
const getOutputDisplayValue = (outputValue: PlainOutputValue): string => {
    switch (outputValue.value.case) {
        case "stringValue":
            return outputValue.value.value;
        case "intValue":
            return outputValue.value.value.toString();
        case "floatValue":
            return outputValue.value.value.toFixed(2);
        case "boolValue":
            return outputValue.value.value ? "true" : "false";
        default:
            return "-";
    }
};

/**
 * Determines if a value should be displayed as a boolean badge
 */
const isBooleanValue = (outputValue: PlainOutputValue): boolean => {
    return outputValue.value.case === "boolValue";
};

/**
 * Gets the numeric value from an output
 */
const getNumericValue = (outputValue: PlainOutputValue): number | undefined => {
    if (outputValue.value.case === "floatValue") {
        return outputValue.value.value;
    }
    if (outputValue.value.case === "intValue") {
        return Number(outputValue.value.value);
    }
    return undefined;
};

/**
 * Calculates percentage within a min/max range
 */
const calculatePercentage = (value: number, min: number, max: number): number => {
    if (max === min) return 100;
    return ((value - min) / (max - min)) * 100;
};

/**
 * Checks if a workflow has any report steps defined
 */
const hasReportSteps = (workflow?: PlainMessage<Workflow>): boolean => {
    if (!workflow?.spec?.report?.steps) {
        return false;
    }
    return workflow.spec.report.steps.some((step) => step.step.case === "report");
};

export const WorkflowExecutionOutputsTable: FC<WorkflowExecutionOutputsTableProps> = ({
    outputs,
    actions,
    workflow,
    workflowExecutionId,
}) => {
    const exportMutation = useExportWorkflowOutputsCSV();

    const handleExportCSV = async () => {
        await exportMutation.mutateAsync({
            workflowExecutionId,
            actions: sortedActions,
        });
    };
    // Build a map of action ID to outputs for quick lookup
    const outputsMap = useMemo(() => {
        const map = new Map<string, PlainWorkflowExecutionActionOutput>();
        for (const output of outputs) {
            map.set(output.actionId, output);
        }
        return map;
    }, [outputs]);

    // Sort actions using the same grouping logic as the sidebar
    const sortedActions = useMemo(() => {
        const groupedActions = groupActions(actions);

        // Same order as sidebar: Active, Failed, Completed, Stopped, Deleted, Pending
        const groupOrder: ActionGroupType[] = [
            ActionGroupType.Active,
            ActionGroupType.Failed,
            ActionGroupType.Completed,
            ActionGroupType.Stopped,
            ActionGroupType.Deleted,
            ActionGroupType.Pending,
        ];

        const sorted: typeof actions = [];
        for (const groupType of groupOrder) {
            const group = groupedActions.get(groupType);
            if (group && group.actions.length > 0) {
                sorted.push(...group.actions);
            }
        }

        return sorted;
    }, [actions]);

    // Extract output keys and schemas from the workflow's report steps
    const { outputSchemas, schemaOutputKeys } = useMemo(() => {
        const schemas = new Map<string, OutputSchema>();
        const keys = new Set<string>();

        if (!workflow?.spec?.report?.steps) {
            return { outputSchemas: schemas, schemaOutputKeys: keys };
        }

        for (const step of workflow.spec.report.steps) {
            if (step.step.case === "report") {
                for (const output of step.step.value.outputs) {
                    const key = output.key;
                    keys.add(key);

                    if (output.schema.case === "integer" || output.schema.case === "float") {
                        const schemaValue = output.schema.value;
                        if (schemaValue.min !== undefined && schemaValue.max !== undefined) {
                            schemas.set(key, {
                                min: schemaValue.min,
                                max: schemaValue.max,
                            });
                        }
                    }
                }
            }
        }
        return { outputSchemas: schemas, schemaOutputKeys: keys };
    }, [workflow]);

    // Combine output keys from actual data and workflow schema
    const outputKeys = useMemo(() => {
        const keys = new Set<string>();

        // Add keys from actual output data
        for (const output of outputs) {
            for (const key of Object.keys(output.values)) {
                keys.add(key);
            }
        }

        // Add keys from workflow schema (if report step exists)
        for (const key of schemaOutputKeys) {
            keys.add(key);
        }

        return Array.from(keys).sort((a, b) => a.localeCompare(b));
    }, [outputs, schemaOutputKeys]);

    // Don't render if no actions
    if (sortedActions.length === 0) {
        return null;
    }

    // Check if we should show the table
    const shouldShowTable = hasReportSteps(workflow) || outputKeys.length > 0;

    // Don't render if workflow has no report steps AND no output data
    if (!shouldShowTable) {
        return null;
    }

    // Count how many actions have outputs
    const actionsWithOutputsCount = sortedActions.filter((action) => outputsMap.has(action.id)).length;

    return (
        <Card variant="bordered" className="border bg-surface-secondary shadow-sm">
            <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    <Text className="text-lg font-semibold text-content-primary">Scorecard</Text>
                    <div className="flex items-center gap-3">
                        <Text className="text-sm text-content-muted">
                            {actionsWithOutputsCount} of {sortedActions.length} actions with outputs
                        </Text>
                        {sortedActions.length > 0 && (
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleExportCSV}
                                disabled={exportMutation.isPending}
                                data-tracking-id="exportcsv"
                            >
                                {exportMutation.isPending ? "Exporting..." : "Export CSV"}
                            </Button>
                        )}
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className="border-b border-border-primary">
                                <th className="px-4 py-3 text-left text-sm font-medium text-content-primary">
                                    Action
                                </th>
                                {outputKeys.map((key) => (
                                    <th
                                        key={key}
                                        className="px-4 py-3 text-center text-sm font-medium text-content-primary"
                                    >
                                        {key}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedActions.map((action, index) => {
                                const actionName = action.metadata?.actionName || action.id;
                                const output = outputsMap.get(action.id);

                                return (
                                    <tr
                                        key={action.id}
                                        className={cn(
                                            "border-b border-border-secondary transition-colors hover:bg-surface-tertiary",
                                            index % 2 === 0 ? "bg-surface-secondary" : "bg-surface-primary",
                                        )}
                                    >
                                        <td className="px-4 py-3">
                                            <Text className="text-sm font-mono text-content-primary">{actionName}</Text>
                                        </td>
                                        {outputKeys.map((key) => {
                                            const outputValue = output?.values[key];

                                            if (!outputValue) {
                                                return (
                                                    <td key={key} className="px-4 py-3 text-center">
                                                        <Text className="text-sm text-content-muted">-</Text>
                                                    </td>
                                                );
                                            }

                                            // Boolean values get badge treatment
                                            if (isBooleanValue(outputValue)) {
                                                const boolVal =
                                                    outputValue.value.case === "boolValue" && outputValue.value.value;
                                                return (
                                                    <td key={key} className="px-4 py-3 text-center">
                                                        <span
                                                            className={cn(
                                                                "inline-flex items-center justify-center rounded-full px-2 py-1 text-xs font-medium",
                                                                boolVal
                                                                    ? "bg-green-500/10 text-green-500"
                                                                    : "bg-red-500/10 text-red-500",
                                                            )}
                                                        >
                                                            {boolVal ? "✓" : "✗"}
                                                        </span>
                                                    </td>
                                                );
                                            }

                                            // Check if this key has a schema with min/max for progress bar
                                            const schema = outputSchemas.get(key);
                                            const numericValue = getNumericValue(outputValue);

                                            if (schema && numericValue !== undefined) {
                                                const percentage = calculatePercentage(
                                                    numericValue,
                                                    schema.min,
                                                    schema.max,
                                                );
                                                return (
                                                    <td key={key} className="px-4 py-3 text-center">
                                                        <div className="flex items-center justify-center gap-2">
                                                            <div className="h-2 w-24 overflow-hidden rounded-full bg-surface-tertiary">
                                                                <div
                                                                    className={cn(
                                                                        "h-full transition-all",
                                                                        percentage >= 80
                                                                            ? "bg-content-green"
                                                                            : percentage >= 50
                                                                              ? "bg-content-orange"
                                                                              : "bg-content-red",
                                                                    )}
                                                                    style={{
                                                                        width: `${Math.min(100, Math.max(0, percentage))}%`,
                                                                    }}
                                                                />
                                                            </div>
                                                            <Text className="w-12 text-right text-sm text-content-muted">
                                                                {getOutputDisplayValue(outputValue)}
                                                            </Text>
                                                        </div>
                                                    </td>
                                                );
                                            }

                                            // Default: plain text display
                                            return (
                                                <td key={key} className="px-4 py-3 text-center">
                                                    <Text className="text-sm text-content-primary">
                                                        {getOutputDisplayValue(outputValue)}
                                                    </Text>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </Card>
    );
};
